import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../supabase';
import '../styles/CreateExperiment.css';

export default function CreateExperiment() {
  // Get current date and time in local timezone for default
  const getCurrentDateTime = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const [formData, setFormData] = useState({
    sample_id: '',
    supplier_name: '',
    supplier_location: '',
    number_of_bags: '',
    inspected_quality: '',
    supplied_date: '',
    experiment_date: getCurrentDateTime(),
    operator_initials: '',
    moisture_percentage: '',
    exact_oil_weight: '',
    initial_naoh_reading: '',
    final_naoh_reading: '',
    normality_of_naoh: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    // Fetch user profile to get initials
    async function fetchUserProfile() {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('initials')
          .eq('id', user.id)
          .single();
        
        if (profile && profile.initials) {
          setFormData(prev => ({
            ...prev,
            operator_initials: profile.initials
          }));
        }
      }
    }
    fetchUserProfile();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevFormData => ({
      ...prevFormData,
      [name]: e.target.type === 'number' ? (value === '' ? '' : parseFloat(value)) : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Calculate FFA before inserting
      const volume_used = formData.final_naoh_reading - formData.initial_naoh_reading;
      const calculated_ffa = ((volume_used * formData.normality_of_naoh * 28.2) / formData.exact_oil_weight).toFixed(2);

      const { data: { user }, error: getUserError } = await supabase.auth.getUser();
      if (getUserError) throw getUserError;
      
      const user_id = user ? user.id : null;

      if (!user_id) {
        throw new Error('You must be logged in to create an experiment.');
      }

      // Handle datetime-local timezone issue by preserving the local date/time
      // Convert to a format that preserves the user's intended date/time
      const [datePart, timePart] = formData.experiment_date.split('T');
      const [year, month, day] = datePart.split('-');
      const [hour, minute] = timePart.split(':');
      
      // Create date object using UTC to avoid timezone conversion
      const experimentDateTime = new Date(Date.UTC(
        parseInt(year), 
        parseInt(month) - 1, // Month is 0-based
        parseInt(day), 
        parseInt(hour), 
        parseInt(minute)
      ));

      const experimentData = {
        ...formData,
        experiment_date: experimentDateTime.toISOString(),
        calculated_ffa, 
        user_id
      };

      const { data, error } = await supabase.from('experiments').insert([experimentData]).select();
      if (error) throw error;

      navigate(`/experiment/${data[0].id}`);
    } catch (error) {
      console.error('Error creating experiment:', error);
      setError(error.message);
      setLoading(false);
    }
  };

  return (
    <div className="create-container">
      <div className="create-header">
        <Link to="/dashboard" className="btn btn-secondary">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="19" y1="12" x2="5" y2="12"></line>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
          Back
        </Link>
        <h1 className="create-title">Create New Experiment</h1>
        <div style={{ width: '100px' }}></div>
      </div>

      {error && (
        <div className="error-message">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="create-form">
        <div className="form-section">
          <h2 className="section-title">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
            </svg>
            Sample Information
          </h2>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="sample_id">Sample ID / Name *</label>
              <input 
                name="sample_id" 
                id="sample_id" 
                placeholder="Enter sample identifier" 
                onChange={handleChange}
                value={formData.sample_id}
                className="form-input" 
                required 
              />
            </div>
            <div className="form-group">
              <label htmlFor="supplier_name">Supplier Name *</label>
              <input 
                name="supplier_name" 
                id="supplier_name" 
                placeholder="Enter supplier name" 
                onChange={handleChange}
                value={formData.supplier_name}
                className="form-input" 
                required 
              />
            </div>
            <div className="form-group">
              <label htmlFor="supplier_location">Supplier Location *</label>
              <input 
                name="supplier_location" 
                id="supplier_location" 
                placeholder="Enter location" 
                onChange={handleChange}
                value={formData.supplier_location}
                className="form-input" 
                required 
              />
            </div>
            <div className="form-group">
              <label htmlFor="number_of_bags">Number of Bags *</label>
              <input 
                name="number_of_bags" 
                id="number_of_bags" 
                placeholder="0" 
                type="number" 
                onChange={handleChange}
                value={formData.number_of_bags}
                className="form-input" 
                required 
              />
            </div>
            <div className="form-group">
              <label htmlFor="inspected_quality">Inspected Quality *</label>
              <input 
                name="inspected_quality" 
                id="inspected_quality" 
                placeholder="Enter quality assessment" 
                onChange={handleChange}
                value={formData.inspected_quality}
                className="form-input" 
                required 
              />
            </div>
            <div className="form-group">
              <label htmlFor="supplied_date">Supplied Date * (dd/mm/yyyy)</label>
              <input 
                name="supplied_date" 
                id="supplied_date" 
                type="date" 
                onChange={handleChange}
                value={formData.supplied_date}
                className="form-input" 
                required 
              />
            </div>
            <div className="form-group">
              <label htmlFor="experiment_date">Experiment Date & Time * (dd/mm/yyyy, HH:mm)</label>
              <input 
                name="experiment_date" 
                id="experiment_date" 
                type="datetime-local" 
                onChange={handleChange}
                value={formData.experiment_date}
                className="form-input" 
                required 
              />
            </div>
            <div className="form-group">
              <label htmlFor="operator_initials">Operator Initials *</label>
              <input 
                name="operator_initials" 
                id="operator_initials" 
                placeholder="Enter initials" 
                onChange={handleChange}
                value={formData.operator_initials}
                className="form-input" 
                required 
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2 className="section-title">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 3v18h18"></path>
              <path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3"></path>
            </svg>
            Sample Preparation
          </h2>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="moisture_percentage">Moisture %</label>
              <input 
                name="moisture_percentage" 
                id="moisture_percentage" 
                placeholder="0.0" 
                type="number" 
                step="any" 
                onChange={handleChange}
                value={formData.moisture_percentage}
                className="form-input" 
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2 className="section-title">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
            </svg>
            Test Parameters
          </h2>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="exact_oil_weight">Exact Oil Weight (W) *</label>
              <input 
                name="exact_oil_weight" 
                id="exact_oil_weight" 
                placeholder="0.00" 
                type="number" 
                step="any" 
                onChange={handleChange}
                value={formData.exact_oil_weight}
                className="form-input" 
                required 
              />
            </div>
            <div className="form-group">
              <label htmlFor="initial_naoh_reading">Initial NaOH Reading *</label>
              <input 
                name="initial_naoh_reading" 
                id="initial_naoh_reading" 
                placeholder="0.00" 
                type="number" 
                step="any" 
                onChange={handleChange}
                value={formData.initial_naoh_reading}
                className="form-input" 
                required 
              />
            </div>
            <div className="form-group">
              <label htmlFor="final_naoh_reading">Final NaOH Reading *</label>
              <input 
                name="final_naoh_reading" 
                id="final_naoh_reading" 
                placeholder="0.00" 
                type="number" 
                step="any" 
                onChange={handleChange}
                value={formData.final_naoh_reading}
                className="form-input" 
                required 
              />
            </div>
            <div className="form-group">
              <label htmlFor="normality_of_naoh">Normality of NaOH (N) *</label>
              <input 
                name="normality_of_naoh" 
                id="normality_of_naoh" 
                placeholder="0.00" 
                type="number" 
                step="any" 
                onChange={handleChange}
                value={formData.normality_of_naoh}
                className="form-input" 
                required 
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2 className="section-title">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
            </svg>
            Additional Information
          </h2>
          <div className="form-group">
            <label htmlFor="notes">Notes / Comments</label>
            <textarea 
              name="notes" 
              id="notes" 
              placeholder="Enter any additional notes or observations..." 
              onChange={handleChange}
              value={formData.notes}
              className="form-textarea"
              rows="4"
            ></textarea>
          </div>
        </div>

        <div className="formula-card">
          <h3 className="formula-title">FFA Calculation Formula</h3>
          <p className="formula-equation">FFA % (as Oleic Acid) = (V × N × 28.2) / W</p>
          <div className="formula-legend">
            <p><strong>V</strong> = Volume Used (Final - Initial Reading)</p>
            <p><strong>N</strong> = Normality of NaOH</p>
            <p><strong>W</strong> = Exact Oil Weight</p>
            <p><strong>28.2</strong> = Molecular weight of Oleic Acid / 10</p>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? (
              <>
                <span className="small-loader"></span>
                <span>Creating...</span>
              </>
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                  <polyline points="17 21 17 13 7 13 7 21"></polyline>
                  <polyline points="7 3 7 8 15 8"></polyline>
                </svg>
                <span>Create Experiment</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}